import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterSection } from '../../components/FilterDropdown';
import type { FilterChip } from '../../components/ActiveFilterChips';
import { fetchKpiAssignableUsers, fetchKpiDashboardFilters } from './kpiDashboardApi';
import { durationPresets, getDurationLabel, type DurationPreset } from '../dashboard/utils/dateHelpers';
import {
  type Group,
  TYPE_OPTIONS,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  KPI_PRIORITY_LABELS,
} from './types';

const KPI_DURATION_OPTIONS = durationPresets
  .filter((p) => p.value !== 'custom')
  .map((p) => ({ value: `preset:${p.value}`, label: p.label }));

const defaultFilterState = {
  duration: 'preset:this_month',
  typeMulti: [] as string[],
  statusMulti: [] as string[],
  priorityFilter: 'All',
  customerId: 'All',
  assignmentGroupIds: [] as string[],
  assignedToMe: '',
  assignedTo: '',
};

function kpiDurationChipLabel(durationValue: string): string {
  if (!durationValue) return 'All Time';
  if (durationValue.startsWith('custom:')) {
    const [, dates] = durationValue.split('custom:');
    const [from, to] = (dates || '').split(',');
    return getDurationLabel('custom', from, to);
  }
  if (durationValue.startsWith('preset:')) {
    const preset = durationValue.replace('preset:', '') as DurationPreset;
    return getDurationLabel(preset);
  }
  return durationValue;
}

export function useKpiDashboardFilters() {
  const [durationFilter, setDurationFilter] = useState<string>(defaultFilterState.duration);
  const [typeMulti, setTypeMulti] = useState<string[]>(defaultFilterState.typeMulti);
  const [statusMulti, setStatusMulti] = useState<string[]>(defaultFilterState.statusMulti);
  const [priorityFilter, setPriorityFilter] = useState<string>(defaultFilterState.priorityFilter);
  const [customerId, setCustomerId] = useState<string>(defaultFilterState.customerId);
  const [assignmentGroupIds, setAssignmentGroupIds] = useState<string[]>(defaultFilterState.assignmentGroupIds);
  const [assignedToMe, setAssignedToMe] = useState<string>(defaultFilterState.assignedToMe);
  const [assignedTo, setAssignedTo] = useState<string>(defaultFilterState.assignedTo);

  const filtersQuery = useQuery({
    queryKey: ['kpi-dashboard-filters'],
    queryFn: fetchKpiDashboardFilters,
    staleTime: 5 * 60 * 1000,
  });

  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  const assignableScopeFilterValues = useMemo(
    () => ({
      duration: durationFilter,
      ticketType: typeMulti,
      state: statusMulti,
      priority: priorityFilter,
      customer: customerId,
      assignmentGroup: assignmentGroupIds,
      assignedToMe: '',
      assignedTo: '',
    }),
    [durationFilter, typeMulti, statusMulti, priorityFilter, customerId, assignmentGroupIds]
  );

  const assignableUsersQueryKey = useMemo(
    () =>
      [
        durationFilter,
        typeMulti.join(','),
        statusMulti.join(','),
        priorityFilter,
        customerId,
        assignmentGroupIds.slice().sort().join(','),
      ].join('|'),
    [durationFilter, typeMulti, statusMulti, priorityFilter, customerId, assignmentGroupIds]
  );

  const usersQuery = useQuery({
    queryKey: ['kpi-dashboard-assignable-users', assignableUsersQueryKey, timezoneOffset],
    queryFn: () => fetchKpiAssignableUsers(assignableScopeFilterValues, timezoneOffset),
    staleTime: 60_000,
  });

  const dashboardFilters = filtersQuery.data;
  const users = usersQuery.data ?? [];

  useEffect(() => {
    if (usersQuery.isLoading || usersQuery.isFetching) return;
    if (assignedTo && !users.some((u) => u.id === assignedTo)) {
      setAssignedTo('');
    }
  }, [usersQuery.isLoading, usersQuery.isFetching, users, assignedTo]);

  const ticketTypeOptions = useMemo(() => {
    if (dashboardFilters?.ticketTypes?.length) {
      return dashboardFilters.ticketTypes.map((t) => ({ value: t, label: t }));
    }
    return TYPE_OPTIONS;
  }, [dashboardFilters?.ticketTypes]);

  const stateOptions = useMemo(() => {
    if (dashboardFilters?.states?.length) {
      return dashboardFilters.states.map((s) => ({ value: s, label: s }));
    }
    return STATUS_OPTIONS;
  }, [dashboardFilters?.states]);

  const priorityChoices = useMemo((): string[] => {
    if (dashboardFilters?.priorities?.length) {
      const inApi = new Set(dashboardFilters.priorities);
      const ordered = KPI_PRIORITY_LABELS.filter((p) => inApi.has(p));
      return ['All', ...ordered];
    }
    return [...PRIORITY_OPTIONS];
  }, [dashboardFilters?.priorities]);

  const customers = useMemo(
    () => dashboardFilters?.customers ?? [],
    [dashboardFilters?.customers]
  );

  const groups: Group[] = useMemo(
    () => dashboardFilters?.assignmentGroups?.map((g) => ({ id: g.id, name: g.name })) ?? [],
    [dashboardFilters?.assignmentGroups]
  );

  const filterKey = useMemo(
    () =>
      [
        durationFilter,
        typeMulti.join(','),
        statusMulti.join(','),
        priorityFilter,
        customerId,
        assignmentGroupIds.slice().sort().join(','),
        assignedToMe,
        assignedTo,
      ].join('-'),
    [
      durationFilter,
      typeMulti,
      statusMulti,
      priorityFilter,
      customerId,
      assignmentGroupIds,
      assignedToMe,
      assignedTo,
    ]
  );

  const filterValues = useMemo(
    () => ({
      duration: durationFilter,
      ticketType: typeMulti,
      state: statusMulti,
      priority: priorityFilter,
      customer: customerId,
      assignmentGroup: assignmentGroupIds,
      assignedToMe,
      assignedTo,
    }),
    [
      durationFilter,
      typeMulti,
      statusMulti,
      priorityFilter,
      customerId,
      assignmentGroupIds,
      assignedToMe,
      assignedTo,
    ]
  );

  const filterSections: FilterSection[] = useMemo(
    () => [
      {
        id: 'duration',
        label: 'Duration',
        type: 'time-range',
        options: KPI_DURATION_OPTIONS,
        placeholder: 'All Time',
        timeRangeCoerceToDayBounds: true,
        timeRangeDefaultFromTime: '00:00',
        timeRangeDefaultToTime: '23:59',
      },
      {
        id: 'assignedToMe',
        label: 'Assigned To Me',
        type: 'single-select',
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
        placeholder: 'All Assignments',
      },
      {
        id: 'assignedTo',
        label: 'Assigned To',
        valuePanelLabel: `Assigned To (${users.length})`,
        type: 'searchable-select',
        options: users.map((u) => ({
          value: u.id,
          label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.id,
        })),
        placeholder: 'All Users',
      },
      {
        id: 'ticketType',
        label: 'Ticket Type',
        type: 'multi-select',
        options: ticketTypeOptions,
      },
      {
        id: 'state',
        label: 'State',
        type: 'multi-select',
        options: stateOptions,
      },
      {
        id: 'priority',
        label: 'Priority',
        type: 'single-select',
        options: priorityChoices.map((p) => ({ value: p, label: p })),
      },
      {
        id: 'customer',
        label: 'Customer',
        type: 'searchable-select',
        options: [{ value: 'All', label: 'All' }, ...customers.map((c) => ({ value: c.id, label: c.accountName }))],
      },
    ],
    [ticketTypeOptions, stateOptions, priorityChoices, customers, users]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (durationFilter) {
      chips.push({ id: 'duration', label: 'Duration', value: kpiDurationChipLabel(durationFilter), color: 'green' });
    }

    if (assignmentGroupIds.length > 0) {
      chips.push({
        id: 'assignmentGroup',
        label: 'Assignment Group',
        value: assignmentGroupIds.map((id) => groups.find((g) => g.id === id)?.name || id).join(', '),
        color: 'gray',
      });
    }

    if (assignedToMe) {
      chips.push({
        id: 'assignedToMe',
        label: 'Assigned To Me',
        value: assignedToMe === 'true' ? 'Yes' : assignedToMe === 'false' ? 'No' : assignedToMe,
        color: 'purple',
      });
    }

    if (assignedTo) {
      const assignedUser = users.find((u) => u.id === assignedTo);
      chips.push({
        id: 'assignedTo',
        label: 'Assigned To',
        value: assignedUser ? `${assignedUser.firstName ?? ''} ${assignedUser.lastName ?? ''}`.trim() || assignedTo : assignedTo,
        color: 'blue',
      });
    }

    if (typeMulti.length > 0) {
      chips.push({
        id: 'ticketType',
        label: 'Type',
        value: typeMulti.map((v) => ticketTypeOptions.find((o) => o.value === v)?.label || v).join(', '),
        color: 'cyan',
      });
    }

    if (statusMulti.length > 0) {
      chips.push({
        id: 'state',
        label: 'State',
        value: statusMulti.map((v) => stateOptions.find((o) => o.value === v)?.label || v).join(', '),
        color: 'purple',
      });
    }

    if (priorityFilter && priorityFilter !== 'All') {
      chips.push({ id: 'priority', label: 'Priority', value: priorityFilter, color: 'orange' });
    }

    if (customerId && customerId !== 'All') {
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customers.find((c) => c.id === customerId)?.accountName || customerId,
        color: 'teal',
      });
    }

    return chips;
  }, [
    durationFilter,
    typeMulti,
    statusMulti,
    priorityFilter,
    customerId,
    assignmentGroupIds,
    assignedToMe,
    assignedTo,
    customers,
    groups,
    users,
    ticketTypeOptions,
    stateOptions,
  ]);

  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'duration':
        setDurationFilter((value as string) || 'preset:this_month');
        break;
      case 'ticketType':
        setTypeMulti(Array.isArray(value) ? value : []);
        break;
      case 'state':
        setStatusMulti(Array.isArray(value) ? value : []);
        break;
      case 'priority':
        setPriorityFilter((value as string) || 'All');
        break;
      case 'customer':
        setCustomerId((value as string) || 'All');
        break;
      case 'assignmentGroup':
        setAssignmentGroupIds(Array.isArray(value) ? value : []);
        break;
      case 'assignedToMe':
        setAssignedToMe((value as string) || '');
        break;
      case 'assignedTo':
        setAssignedTo((value as string) || '');
        break;
      default:
        break;
    }
  };

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'duration':
        setDurationFilter('');
        break;
      case 'ticketType':
        setTypeMulti([]);
        break;
      case 'state':
        setStatusMulti([]);
        break;
      case 'priority':
        setPriorityFilter('All');
        break;
      case 'customer':
        setCustomerId('All');
        break;
      case 'assignmentGroup':
        setAssignmentGroupIds([]);
        break;
      case 'assignedToMe':
        setAssignedToMe('');
        break;
      case 'assignedTo':
        setAssignedTo('');
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setDurationFilter(defaultFilterState.duration);
    setTypeMulti(defaultFilterState.typeMulti);
    setStatusMulti(defaultFilterState.statusMulti);
    setPriorityFilter(defaultFilterState.priorityFilter);
    setCustomerId(defaultFilterState.customerId);
    setAssignmentGroupIds(defaultFilterState.assignmentGroupIds);
    setAssignedToMe(defaultFilterState.assignedToMe);
    setAssignedTo(defaultFilterState.assignedTo);
  };

  const assignmentGroupQuickValue = assignmentGroupIds.length === 1 ? assignmentGroupIds[0] : 'All';

  const handleAssignmentGroupQuickChange = (groupId: string) => {
    if (!groupId || groupId === 'All') {
      setAssignmentGroupIds([]);
      return;
    }
    setAssignmentGroupIds([groupId]);
  };

  return {
    filterKey,
    filterValues,
    filterSections,
    activeFilterChips,
    groups,
    customers,
    assignmentGroupQuickValue,
    handleFilterChange,
    handleAssignmentGroupQuickChange,
    handleRemoveFilterChip,
    handleClearAllFilters,
  };
}
